// src/Components/ImportMarkers/ExcelDialogDocument.js

import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';

import DialogActionBar from './DialogActionBar';
import { MenuUpload } from '../../assets/icons/CustomIconsAdd2';

export default function ExcelDialogDocument({ state, actions }) {
  // Initialize state strictly once per row mount.
  const activeRow = state.parsedData?.find((r) => r.id === state.documentMatchRowId);
  const initialDocs = Array.isArray(activeRow?.documents) ? activeRow.documents : [];

  const [currentDocs, setCurrentDocs] = useState(initialDocs);
  const fileInputRef = useRef(null);

  const submitRef = useRef(null);
  const cancelRef = useRef(null);
  const navPrevRef = useRef(null);
  const navNextRef = useRef(null);
  const navUpRef = useRef(null);
  const navDownRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // AMENDED: Generate Object URLs to pin the files in memory before clearing the input,
      // preventing the OS from destroying the blob before Firebase can upload it.
      files.forEach((f) => {
        if (!f.preview) f.preview = URL.createObjectURL(f);
      });

      setCurrentDocs((prev) => {
        const next = [...prev];
        files.forEach((f) => {
          const fName = f.name || f.fileName;
          if (!next.some((p) => (p.name || p.fileName) === fName)) {
            next.push(f);
          }
        });

        // Auto-commit immediately to the grid
        if (actions.handleDocumentMatch) {
          actions.handleDocumentMatch(next, state.documentMatchRowId);
        }

        return next;
      });
    }
    e.target.value = null;
  };

  const executeRemoveDocument = (docItem) => {
    const targetName = docItem.name || docItem.fileName;

    setCurrentDocs((prev) => {
      const next = prev.filter((d) => (d.name || d.fileName) !== targetName);

      // Auto-commit immediately to the grid
      if (actions.handleDocumentMatch) {
        actions.handleDocumentMatch(next, state.documentMatchRowId);
      }

      return next;
    });

    // If it's a reinstated firebase file, delete it from Storage immediately
    if (docItem.url && actions.deleteDocumentFromStorage) {
      actions.deleteDocumentFromStorage(docItem.url);
    }
  };

  const handleRemoveDocumentClick = (docItem) => {
    const targetName = docItem.name || docItem.fileName;

    if (actions.setConfirmDialog) {
      actions.setConfirmDialog({
        open: true,
        message: `Are you sure you want to remove the document "${targetName}"?`,
        confirmText: 'Remove',
        cancelText: 'Cancel',
        severity: 'warning',
        onConfirm: () => {
          executeRemoveDocument(docItem);
          actions.setConfirmDialog({ open: false });
        },
      });
    } else {
      executeRemoveDocument(docItem);
    }
  };

  const handleOk = () => {
    // OK is disabled, but if triggered (e.g., via Enter key), just close the panel
    if (actions.setDocumentMatchRowId) {
      actions.setDocumentMatchRowId(null);
    }
  };

  const handleCancel = () => {
    if (actions.setDocumentMatchRowId) {
      actions.setDocumentMatchRowId(null);
    }
  };

  const triggerNav = (directionProp, value) => {
    if (actions.setPendingNextColumn) {
      const docColKey = Object.keys(state.rowMappings).find(
        (k) => state.rowMappings[k] === 'DOCUMENTS',
      );
      let docColName = 'Documents';
      if (docColKey) {
        const cr = state.customRows.find((r) => r.id === docColKey);
        if (cr && cr.name !== '-') docColName = cr.name;
        else if (docColKey.startsWith('prop_')) docColName = docColKey.substring(5);
      }
      actions.setPendingNextColumn({
        rowId: state.documentMatchRowId,
        colName: docColName,
        [directionProp]: value,
      });
    }
  };

  const handleNavPrev = () => triggerNav('direction', 'prev');
  const handleNavNext = () => triggerNav('direction', 'next');
  const handleNavUp = () => triggerNav('rowDirection', 'up');
  const handleNavDown = () => triggerNav('rowDirection', 'down');

  useEffect(() => {
    submitRef.current = handleOk;
    cancelRef.current = handleCancel;
    navPrevRef.current = handleNavPrev;
    navNextRef.current = handleNavNext;
    navUpRef.current = handleNavUp;
    navDownRef.current = handleNavDown;
  });

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (!state.documentMatchRowId) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (cancelRef.current) cancelRef.current();
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        if (submitRef.current) submitRef.current();
      }

      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();

        const activeEl = document.activeElement;
        if (activeEl && activeEl.tagName !== 'BODY') {
          activeEl.blur();
        }

        if (e.key === 'ArrowLeft' && navPrevRef.current) navPrevRef.current();
        if (e.key === 'ArrowRight' && navNextRef.current) navNextRef.current();
        if (e.key === 'ArrowUp' && navUpRef.current) navUpRef.current();
        if (e.key === 'ArrowDown' && navDownRef.current) navDownRef.current();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, { capture: true });
    };
  }, [state.documentMatchRowId]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        py: 1,
        pl: 3,
        pr: '52px',
      }}>
      <input
        type='file'
        multiple
        accept='.pdf,.doc,.docx,.xls,.xlsx,.txt'
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Typography
        variant='body2'
        sx={{ fontWeight: 'bold', mb: 2, color: 'text.secondary' }}>
        Assign Documents
      </Typography>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flexGrow: 1,
          border: '1px solid #ccc',
          borderRadius: 1,
          overflowY: 'auto',
        }}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Document Filename</TableCell>
              <TableCell align='right'></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentDocs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align='center'
                  sx={{ py: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                  No documents uploaded yet. Click the upload icon below.
                </TableCell>
              </TableRow>
            ) : (
              currentDocs.map((docItem, idx) => {
                const docName = docItem.name || docItem.fileName;

                return (
                  <TableRow key={idx} hover>
                    <TableCell>{docName}</TableCell>
                    <TableCell align='right'>
                      <IconButton
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveDocumentClick(docItem);
                        }}
                        title='Delete Document'>
                        <CancelIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DialogActionBar
        leftContent={
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            size='small'
            title='Upload Local Documents'
            sx={{ p: 0 }}>
            <MenuUpload
              sx={{ fontSize: 48 }}
              iconScale={8}
              iconX={12}
              iconY={5}
              textSize='22px'
              textY={108}
              label='Upload'
            />
          </IconButton>
        }
        applyToAllChecked={false}
        onApplyToAllChange={() => {}}
        disableApplyToAll={true}
        onCancel={handleCancel}
        disableCancel={false}
        onUp={handleNavUp}
        onDown={handleNavDown}
        onPrev={handleNavPrev}
        onNext={handleNavNext}
        disableNav={false}
        onOk={handleOk}
        disableOk={true} // Grey out OK button since we auto-save real-time
        isOkPressed={false}
        isLoadingOk={false}
      />
    </Box>
  );
}
